<template>
  <header>
    <h2>Recargas y gastos</h2>

    <a-button type="link" @click="handleRefresh" :disabled="loading || expensesLoading">
      Actualizar datos recientes
    </a-button>
  </header>

  <section class="billing">
    <a-card class="balance" :bordered="true">
      <div class="balance__body">
        <IconApiCloud class="balance__icon" :width="46" :height="46" />
        <div class="balance__content">
          <p class="balance__label">
            Saldo disponible de API calls
            <a-tooltip
              title="Indica la cantidad de API calls disponibles para utilizar nuestro MCP y las APIs v2."
            >
              <InfoCircleOutlined class="balance__info-icon" />
            </a-tooltip>
          </p>
          <p v-if="isPostpaid" class="balance__value">
            <span class="balance__value-number">Ilimitado ∞</span>
            <a-tooltip v-if="planTag" :title="planTag.tooltip" placement="top">
              <!-- Trigger en un span plano: un componente como hijo directo de Tooltip
                   puede no recibir los listeners de hover. -->
              <span class="plan-tag-trigger">
                <a-tag
                  class="plan-tag"
                  :class="`plan-tag--${planTag.variant}`"
                  @click="openPlanRestriction = true"
                >
                  {{ planTag.label }}
                </a-tag>
              </span>
            </a-tooltip>
          </p>
          <p v-else class="balance__value">
            <span class="balance__value-number">{{ available === null ? "-" : available.toLocaleString("en-US") }}</span>
            <span class="balance__value-unit">API calls</span>
            <a-tooltip
              title="Cantidad de API calls gratuitas por ciclo de facturación. Las que no se utilicen se descartarán al finalizar el ciclo y la cantidad se reiniciará."
            >
              <InfoCircleOutlined class="balance__info-icon" />
            </a-tooltip>
            <a-tooltip v-if="planTag" :title="planTag.tooltip" placement="top">
              <!-- Trigger en un span plano: un componente como hijo directo de Tooltip
                   puede no recibir los listeners de hover. -->
              <span class="plan-tag-trigger">
                <a-tag
                  class="plan-tag"
                  :class="`plan-tag--${planTag.variant}`"
                  @click="openPlanRestriction = true"
                >
                  {{ planTag.label }}
                </a-tag>
              </span>
            </a-tooltip>
          </p>
        </div>
      </div>
    </a-card>

    <div class="billing__tabs">
      <a-tabs v-model:activeKey="activeTab" class="billing-tabs" destroyInactiveTabPane>
        <a-tab-pane :key="1" tab="Consumos">
          <h3>Mis gastos de saldo</h3>

          <div class="controls">
            <a-form class="form" layout="vertical">
              <a-form-item>
                <a-range-picker
                  v-model:value="dateRange"
                  format="DD/MM/YYYY"
                  valueFormat="YYYY-MM-DD"
                />
              </a-form-item>
            </a-form>

            <a-button :loading="isDownloading" @click="handleDownload">
              Descargar <DownloadOutlined />
            </a-button>
          </div>

          <a-table
            class="consumos-table"
            :columns="consumosColumns"
            :data-source="expenses"
            :loading="loading || expensesLoading"
            :pagination="false"
            row-key="id"
            size="middle"
          >
            <template #headerCell="{ column }">
              {{ column.title }}
              <a-tooltip v-if="column.tooltip" :title="column.tooltip">
                <InfoCircleOutlined />
              </a-tooltip>
            </template>

            <template #bodyCell="{ column, text }">
              <template v-if="column.key === 'date'">{{ text }}</template>
              <template v-else-if="CALLS_COLUMNS.includes(column.key as string)">
                {{ formatCalls(text) }}
              </template>
              <template v-else-if="column.key === 'total_cost'">
                {{ formatCost(text) }}
              </template>
            </template>

            <template #emptyText>
              <div class="empty-container">
                <a-empty :image="simpleImage" description="Aún no tienes consumos registrados" />
              </div>
            </template>

            <template #summary>
              <a-table-summary fixed>
                <a-table-summary-row>
                  <a-table-summary-cell>Total</a-table-summary-cell>
                  <a-table-summary-cell v-if="isPostpaid">
                    {{ formatCalls(totalCalls) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell>
                    {{ formatCalls(totalFreeCalls) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell v-if="isPostpaid">
                    {{ formatCalls(totalPaidCalls) }}
                  </a-table-summary-cell>
                  <a-table-summary-cell>
                    {{ formatCost(totalCost) }}
                  </a-table-summary-cell>
                </a-table-summary-row>
              </a-table-summary>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </div>

    <BalanceRechargeCard
      :minVal="5"
      :isInfinite="isPostpaid"
      :type="ProductType.ApiCalls"
      ref="rechargeRef"
      :slots="slots"
      class="billing__payment"
      @update="handleRefresh"
    />
  </section>

  <ApiCloudPlanRestrictionModal
    v-model:open="openPlanRestriction"
    :plan="currentPlan"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { message, Empty } from "ant-design-vue";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons-vue";
import _ from "lodash";
import dayjs from "dayjs";

import { ProductType } from "@/@types/settings/billing";
import { useApiCloudUsage } from "@/app/shared/composables/useApiCloudUsage";
import BalanceRechargeCard from "@/app/settings/components/cards/BalanceRechargeCard.vue";
import IconApiCloud from "@/app/common/components/atoms/IconApiCloud.vue";
import SlotManager from "@/app/settings/services/slots/SlotManager";
import { ISlots } from "@/@types/settings/slots";
import httpManager from "@/app/shared/utils/httpManager";
import { downloadFile } from "@/app/shared/utils/file";
import { useStore } from "vuex";
import { PlanType, PlanEspecial } from "@/app/common/types";
import ApiCloudPlanRestrictionModal from "@/app/common/components/modals/ApiCloudPlanRestrictionModal.vue";

const simpleImage = Empty.PRESENTED_IMAGE_SIMPLE;

const manager = new SlotManager();
const slots = ref<ISlots>();
const rechargeRef = ref();

const activeTab = ref(1);

const store = useStore();
const user = computed(() => store.getters["getDataUser"]);
const dataPlan = computed(() => store.getters["getDataPlanDetails"]);
const openPlanRestriction = ref(false);

// El id del plan viaja siempre en el usuario; el detalle del plan (`configuration`) solo se
// carga cuando el tenant tiene un plan config asociado. Se prefiere el usuario y se cae al
// detalle para no depender de un dato que puede faltar.
const currentPlan = computed<number | null>(
  () => user.value?.change_custom_plan?.plan ?? dataPlan.value?.plan ?? null
);

/**
 * CTA comercial del saldo de API calls. Solo se muestra cuando el plan no incluye la
 * funcionalidad: si está activa, el cliente ya consume API calls y el tag no aplica.
 * Especial y All in One se resuelven personalizando; los planes inferiores, con un upgrade.
 */
const planTag = computed(() => {
  const plan = currentPlan.value;
  if (plan === null) return null;
  if (dataPlan.value?.configuration?.api_cloud_token?.active) return null;

  if (plan === PlanEspecial || plan === PlanType.AllInOne) {
    return {
      label: "Personalizar",
      variant: "personalize",
      tooltip: "Solicita personalizar tu plan para consumir las API calls.",
    };
  }

  return {
    label: "Upgrade",
    variant: "upgrade",
    tooltip: "Solicita un upgrade de tu plan para consumir las API calls.",
  };
});

const { loading, fetchUsages, usages, available } = useApiCloudUsage();

const todayDate = dayjs().format("YYYY-MM-DD");
// La tabla de consumos es por día; el rango por defecto cubre el mes en curso para
// que se vean varios días (con un solo día se mostraría una única fila).
const monthStart = dayjs().startOf("month").format("YYYY-MM-DD");
const dateRange = ref<[string, string]>([monthStart, todayDate]);
const expenses = ref<any[]>([]);
const expensesLoading = ref(false);
const isDownloading = ref(false);

const CALLS_COLUMNS = ["total_calls_consumed", "free_calls_consumed", "paid_calls_consumed"];

// Enum del backend (`helpers/choices.py`): 1 = Prepago, 2 = Postpago.
const API_CLOUD_PRE_PAYMENT_BILLING_TYPE = 1;

// Prepago/postpago de API Cloud. Fuente primaria: el propio dato de API Cloud que ya viaja en
// `GET /billing/charges/api-cloud/` (`is_pre_payment`, con `api_cloud_type_billing` como
// respaldo). Para un tenant cuyos `slot_type_billing` y tipo de API Cloud divergen, el slot daría
// la variante equivocada, así que NO es la fuente.
const apiPrePayment = computed<boolean | null>(() => {
  const row = usages.value[0];
  if (row && typeof row.is_pre_payment === "boolean") return row.is_pre_payment;

  const billingType = row?.api_cloud_type_billing;
  if (typeof billingType === "number") return billingType === API_CLOUD_PRE_PAYMENT_BILLING_TYPE;

  return null;
});

// Respaldo legado: solo se consulta si el campo propio de API Cloud está genuinamente ausente en
// la respuesta de consumo.
const isPostpaid = computed(() => {
  if (apiPrePayment.value !== null) return !apiPrePayment.value;
  return slots.value?.slot_type_billing === "Postpago";
});

const prepaidColumns = [
  {
    title: "Fecha",
    dataIndex: "date",
    key: "date",
    width: "40%",
  },
  {
    title: "API calls gratuitas consumidas",
    dataIndex: "free_calls_consumed",
    key: "free_calls_consumed",
    tooltip: "Indica el consumo total de API calls gratuitas. Se cuenta toda solicitud al API Cloud, como de nuestro MCP y las APIs v2.",
    width: "40%",
  },
  {
    title: "Costo total",
    dataIndex: "total_cost",
    key: "total_cost",
    tooltip: "Indica el costo total por las API calls consumidas.",
    width: "20%",
  },
];

const postpaidColumns = [
  { title: "Fecha", dataIndex: "date", key: "date", width: "16%" },
  {
    title: "Total de API calls consumidas",
    dataIndex: "total_calls_consumed",
    key: "total_calls_consumed",
    tooltip: "Indica el consumo total de API calls (gratuitas y de cobro). Se cuenta toda solicitud al API Cloud, como de nuestro MCP y las APIs v2.",
    width: "26%",
  },
  {
    title: "API calls gratuitas consumidas",
    dataIndex: "free_calls_consumed",
    key: "free_calls_consumed",
    tooltip: "Indica el consumo de API calls gratuitas que BeexCC brinda mensualmente a tu plan.",
    width: "26%",
  },
  {
    title: "API calls de cobro consumidas",
    dataIndex: "paid_calls_consumed",
    key: "paid_calls_consumed",
    tooltip: "Indica el consumo de API calls que fueron compradas o facturadas. El precio por API call es de $0.002.",
    width: "26%",
  },
  {
    title: "Costo total",
    dataIndex: "total_cost",
    key: "total_cost",
    tooltip: "Indica la suma de los costos por consumir API calls gratuitas y de cobro según su costo individual correspondiente.",
    width: "16%",
  },
];

const consumosColumns = computed(() => (isPostpaid.value ? postpaidColumns : prepaidColumns));

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Única fuente de verdad de la derivación. El backend (GET /billing/charges/api-cloud/)
// devuelve { period, calls_count, included_calls, overage_calls, overage_price, total_price, free_calls }
// y no comparte ninguno de los nombres que la tabla espera: la normalización vive aquí y la
// consumen la tabla, los totales y el CSV, para que no puedan discrepar.
const deriveExpenseRow = (raw: any) => {
  const totalCalls = toFiniteNumber(raw?.calls_count);
  // `free_calls` es el tramo gratuito del plan. Si es null no hay configuración de API Cloud:
  // las columnas derivadas quedan vacías y NUNCA se presentan como 0.
  const freeCalls = toFiniteNumber(raw?.free_calls);
  const overagePrice = toFiniteNumber(raw?.overage_price);

  const freeCallsConsumed =
    freeCalls === null || totalCalls === null ? null : Math.min(totalCalls, freeCalls);
  const paidCallsConsumed =
    freeCalls === null || totalCalls === null ? null : Math.max(0, totalCalls - freeCalls);

  // `period` ("YYYY-MM") se renderiza como "MM/YYYY": es un formato, no copy nuevo.
  const period = raw?.period ? dayjs(String(raw.period)) : null;

  return {
    // `row-key="id"` de la tabla necesita un valor único por fila: `period` lo es
    // (una fila por período). Sin esto todas las filas quedaban con key `undefined`.
    id: raw?.period ? String(raw.period) : "",
    date: period?.isValid() ? period.format("MM/YYYY") : "",
    total_calls_consumed: totalCalls,
    free_calls_consumed: freeCallsConsumed,
    paid_calls_consumed: paidCallsConsumed,
    // El costo se deriva del tramo gratuito (`free_calls`) y NUNCA de `total_price`.
    // `total_price` y `overage_calls` se miden sobre el límite EFECTIVO (p. ej. 12,500),
    // por eso con 10,003 calls consumidas el backend devuelve `total_price: 0`: usarlo como
    // "Costo total" mostraría $0 para siempre. No cambiar esta derivación.
    total_cost:
      paidCallsConsumed === null || overagePrice === null
        ? null
        : paidCallsConsumed * overagePrice,
  };
};

const deriveExpenses = (data: unknown): any[] =>
  Array.isArray(data) ? data.map(deriveExpenseRow) : [];

// Consumos por día (`GET /billing/charges/api-cloud/usage-daily/`): una fila por día.
// El reparto gratis/facturable lo resuelve el backend porque la franquicia se consume
// acumulada dentro del mes: depende de los días previos del período, no solo del día.
const deriveDailyRow = (raw: any) => {
  const day = raw?.date ? dayjs(String(raw.date)) : null;
  const totalPrice =
    raw?.total_price === null || raw?.total_price === undefined
      ? null
      : Number(raw.total_price);

  return {
    id: raw?.date ? String(raw.date) : "",
    date: day?.isValid() ? day.format("DD/MM/YYYY") : "",
    total_calls_consumed: toFiniteNumber(raw?.calls_count),
    free_calls_consumed: raw?.free_calls_consumed ?? null,
    paid_calls_consumed: raw?.paid_calls_consumed ?? null,
    total_cost: Number.isFinite(totalPrice as number) ? totalPrice : null,
  };
};

const deriveDailyRows = (data: unknown): any[] => {
  const days = (data as { days?: unknown } | null | undefined)?.days;
  return Array.isArray(days) ? days.map(deriveDailyRow) : [];
};

// Formato de presentación compartido por la tabla, el total y el CSV: desconocido => celda vacía.
const formatCalls = (value: unknown) =>
  value === null || value === undefined || value === ""
    ? ""
    : Number(value).toLocaleString("en-US");

const formatCost = (value: unknown) =>
  value === null || value === undefined || value === "" ? "" : `$${Number(value).toFixed(2)}`;

// Si alguna fila tiene un valor desconocido (p. ej. `free_calls` null), el total también es
// desconocido en vez de fabricar un 0.
const sumField = (field: string): number | null => {
  let total = 0;
  for (const item of expenses.value) {
    const value = item[field];
    if (value === null || value === undefined) return null;
    total += Number(value) || 0;
  }
  return total;
};

const totalCalls = computed(() => sumField("total_calls_consumed"));
const totalFreeCalls = computed(() => sumField("free_calls_consumed"));
const totalPaidCalls = computed(() => sumField("paid_calls_consumed"));
const totalCost = computed(() => sumField("total_cost"));

const fetchExpenses = async () => {
  expensesLoading.value = true;
  try {
    const params: any = {};
    if (dateRange.value?.[0] && dateRange.value?.[1]) {
      params.start_date = dateRange.value[0];
      params.end_date = dateRange.value[1];
    }
    // Detalle por día: es lo que muestra la tabla. La llamada va en su propio try/catch
    // para que un endpoint no disponible (p. ej. un backend anterior al release del detalle
    // diario) caiga al listado por período en vez de dejar la tabla vacía.
    try {
      const daily = await httpManager.get("/billing/charges/api-cloud/usage-daily/", { params });
      const dailyRows = deriveDailyRows(daily.data);
      if (dailyRows.length) {
        expenses.value = dailyRows;
        return;
      }
    } catch (error) {
      console.warn("Consumos diarios no disponibles; se usa el listado por período", error);
    }

    // Sin detalle diario para el rango (períodos anteriores al release de la bitácora o
    // endpoint no disponible): se mantiene el listado por período.
    const response = await httpManager.get("/billing/charges/api-cloud/", { params });
    // La forma de la respuesta no se asume: se normaliza campo por campo en deriveExpenseRow.
    expenses.value = deriveExpenses(response.data);
  } catch (error) {
    expenses.value = [];
  } finally {
    expensesLoading.value = false;
  }
};

const handleRefresh = async () => {
  await Promise.all([fetchUsages(), fetchExpenses()]);
};

const handleDownload = async () => {
  if (!expenses.value?.length) {
    message.warning("No hay datos para descargar");
    return;
  }

  isDownloading.value = true;
  try {
    const params: any = {};
    if (dateRange.value?.[0] && dateRange.value?.[1]) {
      params.start_date = dateRange.value[0];
      params.end_date = dateRange.value[1];
    }

    // El Excel (.xlsx) lo genera el backend con celdas numéricas tipadas: el CSV que se
    // armaba en el navegador hacía que Excel reinterpretara los valores y perdiera los
    // ceros (y los montos quedaban como texto).
    const response = await httpManager.getBlob("/billing/charges/api-cloud/download/", {
      params,
    });
    downloadFile(response, "consumos-api-calls.xlsx", { useResponseFileName: true });
    message.success("La descarga se realizó correctamente");
  } catch (error) {
    message.error("Ha ocurrido un error al exportar");
    console.error(error);
  } finally {
    isDownloading.value = false;
  }
};

watch(
  dateRange,
  _.debounce(() => {
    fetchExpenses();
  }, 500)
);

const fetchSlots = async () => {
  const response = await manager.getInfoSlots();
  slots.value = response;
};

onMounted(() => {
  fetchUsages();
  fetchExpenses();
  fetchSlots();
});
</script>

<style lang="sass" scoped>
header
  display: flex
  align-items: baseline
  margin-bottom: 12px

h2
  font-size: 20px
  font-weight: 600
  line-height: 24px
  margin: 0

.plan-tag-trigger
  display: inline-flex
  align-self: center

.plan-tag
  margin: 0
  cursor: pointer
  font-weight: 500

  &--personalize
    background: #E6FFFB
    border: 1px solid #87E8DE
    color: #08979C

  &--upgrade
    background: #F9F0FF
    border: 1px solid #D3ADF7
    color: #722ED1

.billing
  display: grid
  grid-template-columns: repeat(2, minmax(400px, 1fr)) minmax(250px, 450px)
  grid-template-rows: 95px minmax(0, 1fr)
  gap: 8px 20px
  height: calc(100% - 44px)

  &__tabs
    grid-column: 1 / 3
    grid-row: 2 / 3

  &__payment
    grid-column: 3
    grid-row: 1 / 3

h3
  font-size: 16px
  font-weight: 600
  margin: 20px 0 12px

.balance
  grid-column: 1 / 3
  height: 95px
  border-radius: 8px

  :deep(.ant-card-body)
    padding: 16px 20px
    height: 100%
    display: flex
    align-items: center

  &__body
    display: flex
    align-items: center
    gap: 16px

  &__icon
    width: 46px
    height: 46px
    flex-shrink: 0

  &__content
    display: flex
    align-items: center
    gap: 24px

  &__label
    margin: 0
    max-width: 130px
    font-size: 13px
    line-height: 18px
    color: $gray-8

  &__value
    margin: 0
    display: flex
    align-items: baseline
    gap: 6px

  &__value-number
    font-size: 30px
    font-weight: 700
    line-height: 1.1
    color: $gray-10

  &__value-unit
    font-size: 14px
    color: $gray-8
    font-weight: 400

  &__info-icon
    margin-left: 4px
    color: $gray-6
    font-size: 13px
    cursor: pointer

.controls
  margin-bottom: 12px
  display: flex
  align-items: center
  justify-content: space-between

.form
  display: flex
  gap: 12px

:deep(.ant-form-item)
  margin: 0

.consumos-table
  :deep(.ant-table-summary td)
    background: $gray-3
    font-weight: 400

  :deep(.ant-table-summary td:first-child),
  :deep(.ant-table-summary td:last-child)
    font-weight: 600

.empty-container
  padding: 32px 0
</style>
